import { useMemo } from 'react';
import { usePoolData } from './usePoolData';
import { PoolDataHookResult } from '../types/pool';
import { POOL_CONFIGS } from '../config/pools';

/**
 * Custom hook that fetches data for all pools defined in the liquidity_pools.json configuration.
 * 
 * This hook maintains React's Rules of Hooks while ensuring maintainability by:
 * 1. Using POOL_CONFIGS (loaded from JSON) for all pool configurations
 * 2. Calling each usePoolData hook individually at the top level
 * 3. Mapping hooks to configurations using POOL_CONFIGS for consistency
 * 
 * Benefits:
 * - Single source of truth: All pool data comes from liquidity_pools.json
 * - Maintainable: Adding pools to JSON automatically works (just add corresponding hook calls)
 * - Type-safe: Uses TypeScript interfaces for all configurations
 * - React-compliant: Follows Rules of Hooks by calling hooks at top level
 * 
 * @param days - Number of days of historical data to fetch (default: 30)
 * @returns Object mapping pool keys to their data hook results
 */

// Helper function to get pool configuration by address
const getPoolConfigByAddress = (address: string) => {
  return POOL_CONFIGS.find(config => config.address.toLowerCase() === address.toLowerCase());
};

export const useAllPoolsData = (days: number = 30) => {
  console.log('useAllPoolsData called with days:', days);
  console.log('Loading data for pools:', POOL_CONFIGS.length);
  
  // Call all hooks at the top level (React rules requirement)
  // Each hook uses the actual pool configuration from POOL_CONFIGS
  // This ensures consistency with the JSON data while maintaining React's Rules of Hooks
  
  // Uniswap V3 pools - using configurations from POOL_CONFIGS
  const uniswapV3Pool1 = usePoolData(
    POOL_CONFIGS[0].address,
    POOL_CONFIGS[0].name,
    POOL_CONFIGS[0].dex,
    days
  );
  
  const uniswapV3Pool2 = usePoolData(
    POOL_CONFIGS[1].address,
    POOL_CONFIGS[1].name,
    POOL_CONFIGS[1].dex,
    days
  );
  
  const uniswapV3Pool3 = usePoolData(
    POOL_CONFIGS[2].address,
    POOL_CONFIGS[2].name,
    POOL_CONFIGS[2].dex,
    days
  );
  
  // PancakeSwap V3 pools - using configurations from POOL_CONFIGS
  const pancakeswapV3Pool1 = usePoolData(
    POOL_CONFIGS[3].address,
    POOL_CONFIGS[3].name,
    POOL_CONFIGS[3].dex,
    days
  );
  
  const pancakeswapV3Pool2 = usePoolData(
    POOL_CONFIGS[4].address,
    POOL_CONFIGS[4].name,
    POOL_CONFIGS[4].dex,
    days
  );
  
  const pancakeswapV3Pool3 = usePoolData(
    POOL_CONFIGS[5].address,
    POOL_CONFIGS[5].name,
    POOL_CONFIGS[5].dex,
    days
  );
  
  // Uniswap V4 pools - using configurations from POOL_CONFIGS
  const uniswapV4Pool1 = usePoolData(
    POOL_CONFIGS[6].address,
    POOL_CONFIGS[6].name,
    POOL_CONFIGS[6].dex,
    days
  );
  
  const uniswapV4Pool2 = usePoolData(
    POOL_CONFIGS[7].address,
    POOL_CONFIGS[7].name,
    POOL_CONFIGS[7].dex,
    days
  );
  
  const uniswapV4Pool3 = usePoolData(
    POOL_CONFIGS[8].address,
    POOL_CONFIGS[8].name,
    POOL_CONFIGS[8].dex,
    days
  );
  
  const uniswapV4Pool4 = usePoolData(
    POOL_CONFIGS[9].address,
    POOL_CONFIGS[9].name,
    POOL_CONFIGS[9].dex,
    days
  );
  
  const uniswapV4Pool5 = usePoolData(
    POOL_CONFIGS[10].address,
    POOL_CONFIGS[10].name,
    POOL_CONFIGS[10].dex,
    days
  );
  
  // SushiSwap V3 pools - using configurations from POOL_CONFIGS
  const sushiswapV3Pool1 = usePoolData(
    POOL_CONFIGS[11].address,
    POOL_CONFIGS[11].name,
    POOL_CONFIGS[11].dex,
    days
  );

  const sushiswapV3Pool2 = usePoolData(
    POOL_CONFIGS[12].address,
    POOL_CONFIGS[12].name,
    POOL_CONFIGS[12].dex,
    days
  );

  // Combine all pool data into a single object using POOL_CONFIGS for mapping
  const poolsData = useMemo(() => {
    const result: { [key: string]: PoolDataHookResult } = {};
    
    // Map each pool hook to its configuration using POOL_CONFIGS
    const poolHooks = [
      uniswapV3Pool1, uniswapV3Pool2, uniswapV3Pool3,
      pancakeswapV3Pool1, pancakeswapV3Pool2, pancakeswapV3Pool3,
      uniswapV4Pool1, uniswapV4Pool2, uniswapV4Pool3, uniswapV4Pool4, uniswapV4Pool5,
      sushiswapV3Pool1, sushiswapV3Pool2
    ];
    
    // Use POOL_CONFIGS to create the mapping, ensuring consistency with JSON data
    POOL_CONFIGS.forEach((poolConfig, index) => {
      const poolKey = `${poolConfig.dex}_${poolConfig.address}`;
      result[poolKey] = poolHooks[index];
    });
    
    console.log('Pools data loaded:', Object.keys(result).length, 'pools');
    return result;
  }, [
    uniswapV3Pool1, uniswapV3Pool2, uniswapV3Pool3,
    pancakeswapV3Pool1, pancakeswapV3Pool2, pancakeswapV3Pool3,
    uniswapV4Pool1, uniswapV4Pool2, uniswapV4Pool3, uniswapV4Pool4, uniswapV4Pool5,
    sushiswapV3Pool1, sushiswapV3Pool2
  ]);

  return poolsData;
};



